// frontend/src/components/UI/ProgressModal.js
/**
 * Non-dismissible progress modal for long-running operations.
 * Follows design-system-reference.md § Modal System + § Progress Indicators.
 * Uses Portal, modal-overlay, modal-container-sm, admin theme (red accents).
 *
 * Props:
 *   isOpen          – boolean
 *   title           – string  (modal heading)
 *   phases          – [{ label: string, icon?: ReactNode }]
 *   currentPhase    – number  (0-based index of the active phase)
 *   progress        – { current: number, total: number } | null
 *   statusMessage   – string  (e.g. "Processing attendance: 45/120")
 *   error           – string | null  (if set, shows error state with dismiss)
 *   department      – string | null  (e.g. "Operations")
 *   currentEmployee – string | null  (e.g. "John Smith (W0149)")
 *   subPhase        – string | null  (e.g. "processing", "saving", "locks")
 *   onDismissError  – () => void  (callback when user dismisses after error)
 */

import React from 'react';
import { CheckCircle, Loader2, Circle, AlertCircle, User, Building2 } from 'lucide-react';
import Portal from './Portal';

const SUB_PHASE_LABELS = {
  locks: 'Checking locks',
  querying: 'Querying records',
  processing: 'Processing attendance',
  saving: 'Saving records',
};

const ProgressModal = ({
  isOpen,
  title = 'Processing',
  phases = [],
  currentPhase = 0,
  progress = null,
  statusMessage = '',
  error = null,
  department = null,
  currentEmployee = null,
  subPhase = null,
  onDismissError,
}) => {
  if (!isOpen) return null;

  const percentage = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : null;

  const subPhaseLabel = subPhase ? SUB_PHASE_LABELS[subPhase] || subPhase : null;

  return (
    <Portal>
      {/* Overlay — no onClick dismiss (non-dismissible) */}
      <div
        className="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="progress-modal-title"
      >
        <div
          className="modal-container modal-container-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header">
            <h3 id="progress-modal-title" className="modal-title">{title}</h3>
          </div>

          {/* Content */}
          <div className="modal-content">
            {/* Context bar: department + sub-phase */}
            {!error && (department || subPhaseLabel) && (
              <div className="flex items-center gap-3 mb-4 px-3 py-2 bg-gray-50 rounded-lg text-sm">
                {department && (
                  <span className="inline-flex items-center gap-1.5 text-gray-700 font-medium">
                    <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    {department}
                  </span>
                )}
                {department && subPhaseLabel && (
                  <span className="text-gray-300">|</span>
                )}
                {subPhaseLabel && (
                  <span className="text-gray-500">{subPhaseLabel}</span>
                )}
              </div>
            )}

            {/* Phase stepper */}
            <div className="space-y-3 mb-5">
              {phases.map((phase, idx) => {
                const isComplete = idx < currentPhase;
                const isActive = idx === currentPhase && !error;
                const isPending = idx > currentPhase;
                const isErrorPhase = idx === currentPhase && !!error;

                return (
                  <div key={idx} className="flex items-center gap-3">
                    {isComplete && (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    )}
                    {isActive && (
                      <Loader2 className="w-5 h-5 text-red-600 animate-spin flex-shrink-0" />
                    )}
                    {isErrorPhase && (
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    {isPending && (
                      <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}

                    <span
                      className={`text-sm ${
                        isComplete
                          ? 'text-green-700 font-medium'
                          : isActive
                            ? 'text-gray-900 font-medium'
                            : isErrorPhase
                              ? 'text-red-700 font-medium'
                              : 'text-gray-400'
                      }`}
                    >
                      {phase.label}
                      {isActive && progress && progress.total > 0 && (
                        <span className="ml-2 text-gray-500 font-normal">
                          ({progress.current}/{progress.total} employees)
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Progress bar — only shown for determinate phases */}
            {!error && percentage !== null && (
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="text-gray-900 font-medium">{percentage}%</span>
                </div>
                <div className="progress">
                  <div
                    className="progress-bar-admin"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Indeterminate loader for phases without progress data */}
            {!error && percentage === null && (
              <div className="flex items-center justify-center py-2 mb-4">
                <div className="loading-spinner-admin loading-spinner-lg" />
              </div>
            )}

            {/* Current employee being processed */}
            {!error && currentEmployee && (
              <div className="flex items-center gap-2 justify-center mb-2 text-sm text-gray-600">
                <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate max-w-[280px]">{currentEmployee}</span>
              </div>
            )}

            {/* Status message */}
            {!error && statusMessage && (
              <p className="text-xs text-gray-500 text-center">
                {statusMessage}
              </p>
            )}

            {/* Error state */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Footer — only shown on error so user can dismiss */}
          {error && onDismissError && (
            <div className="modal-footer">
              <button
                type="button"
                onClick={onDismissError}
                className="btn btn-admin-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default ProgressModal;
